import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OrdersService } from '../../services/orders.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { OrderGeneralInfoComponent } from '../order-general-info/order-general-info.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { TabsComponent } from '@app/components/shared/tabs/tabs.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { OrderResponseDto } from '@app/utils/interfaces/order.interface';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import {
  OrdersModalMode,
  OrderItemModalSection,
  SECTIONS,
} from '@app/utils/types/order-item-modal-section.type';
import { OrderStatusHistoryComponent } from '../order-status-history/order-status-history.component';

@Component({
  selector: 'app-order-item-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    IconComponent,
    MatProgressSpinnerModule,
    TabsComponent,
    OrderGeneralInfoComponent,
    OrderStatusHistoryComponent,
  ],
  templateUrl: './order-item-modal.component.html',
  styleUrl: './order-item-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderItemModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<OrderItemModalComponent>);
  private readonly errDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrdersService);

  private readonly modeHandlers: Record<OrdersModalMode, () => void> = {
    view: () => this.viewOrderItem(this.dialogData.id!),
  };

  protected isLoading = signal(false);
  public orderItem = signal<OrderResponseDto | null>(null);
  public readonly dialogData = inject<{ id?: string; mode: OrdersModalMode }>(MAT_DIALOG_DATA);
  public readonly tabs = SECTIONS;
  public readonly iconName = IconName;

  public activeTab = signal<OrderItemModalSection>('general');

  public ngOnInit(): void {
    this.modeHandlers[this.dialogData.mode]();
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public viewOrderItem(id: string): void {
    this.isLoading.set(true);

    this.orderService
      .getOrderById(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.orderItem.set(response.data);
        },
        error: () => {
          this.openErrorModal();
          this.onClose();
        },
      });
  }

  private openErrorModal(message?: string): void {
    this.errDialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'orders.table.errors.title',
        description: message || 'orders.table.errors.ordersLoadFail',
      },
    });
  }
}
